import { Button, InputAdornment, Stack, Typography, Autocomplete, TextField, CircularProgress, Collapse, MenuItem, Select } from "@mui/material";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { KeyboardArrowDown } from "@mui/icons-material";
import debounce from 'lodash/debounce';

import FormTextField from "../formElements/textfield";
import { checkDomain } from "../../services/createTenant";
import { getDomainSuggestions } from "../../services/getDomainSuggestions";
import { LoadingButton } from "@mui/lab";
import { sendOtp, verifyOtp } from "../../services/otp";

function CompanyInfoComp({ activeStep, handleBack, setActiveStep, isLoading }) {
  const { trigger, setError, clearErrors, getValues, setValue, watch } = useFormContext();
  const domainUrl = window.location.hostname;
  const [domainSuggestions, setDomainSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wasRecentlyCalled = useRef(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [sendOtpError, setSendOtpError] = useState("");
  const [otp, setOtp] = useState("");
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const OTP_LENGTH = 6;
  const RESEND_DURATION = 30;
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [secondsLeft, setSecondsLeft] = useState(0);
  const digitRefs = useRef([]);
  const phoneValue = watch("phone_number");
  const [countryCode, setCountryCode] = useState("91");
  const isPhoneFormatValid = React.useMemo(() => {
    if (!phoneValue) return false;
    const cleaned = phoneValue.replace(/\D/g, "");
    if (cleaned.length !== 10) return false;
    return countryCode === "1" || countryCode === "91";
  }, [phoneValue, countryCode]);

  const handleCompanyNameChange = (e) => {
    setValue("company_name", e.target.value);
    clearErrors("company_name");
    wasRecentlyCalled.current = false;
    debouncedHandleCompanyNameBlur();
  };

  const validateEmail = (value) => {
    const trimmed = (value || "").trim();
    if (!trimmed) {
      setError("email", {
        type: "manual",
        message: "Email is required",
      });
      return;
    }

    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if (!isValid) {
      setError("email", {
        type: "manual",
        message: "Please enter a valid email address",
      });
    } else {
      clearErrors("email");
    }
  };

  const debouncedHandleCompanyNameBlur = useCallback(
    debounce(() => {
      handleCompanyNameBlur();
      wasRecentlyCalled.current = true;
    }, 3000),
    []
  );

  useEffect(() => {
    // keep form value in sync with selected country code for submit payload
    setValue("phone_country_code", countryCode);
  }, [countryCode, setValue]);

  useEffect(() => {
    setOtpSent(false);
    setOtpVerified(false);
    setOtp("");
    setSendOtpError("");
    setOtpError("");
    setOtpDigits(Array(OTP_LENGTH).fill(""));
    setSecondsLeft(0);
  }, [phoneValue, countryCode]);

  useEffect(() => {
    if (secondsLeft <= 0) {
      return;
    }

    const interval = setInterval(() => {
      setSecondsLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [secondsLeft]);

  const handleSendOtp = async () => {
    const isPhoneValid = await trigger("phone_number");
    if (!isPhoneValid) {
      return;
    }

    const localPhone = getValues("phone_number") || "";
    const cleanedLocal = localPhone.replace(/\D/g, "");
    const fullPhone = `${countryCode}${cleanedLocal}`;
    setOtpDigits(Array(OTP_LENGTH).fill(""));
    setIsSendingOtp(true);
    setSendOtpError("");
    try {
      const res = await sendOtp(fullPhone);
      setValue("user_uuid", res?.data?.data?.responseData?.user_uuid);
      setOtpSent(true);
      setSecondsLeft(RESEND_DURATION);
    } catch (err) {
      console.error("send otp failed", err);
      setSendOtpError(
        err?.response?.data?.message || "Failed to send OTP. Please try again."
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleDigitChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const updatedDigits = [...otpDigits];
    updatedDigits[index] = digit;
    setOtpDigits(updatedDigits);
    setOtp(updatedDigits.join(""));
    if (digit && index < OTP_LENGTH - 1) {
      digitRefs.current[index + 1]?.focus();
    }
  };

  const handleDigitPaste = (index, event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text");
    const digits = pastedData.replace(/\D/g, "").slice(0, OTP_LENGTH);
    
    if (digits.length > 0) {
      const updatedDigits = [...otpDigits];
      // Fill digits starting from the current index
      for (let i = 0; i < digits.length && (index + i) < OTP_LENGTH; i++) {
        updatedDigits[index + i] = digits[i];
      }
      setOtpDigits(updatedDigits);
      setOtp(updatedDigits.join(""));
      
      // Focus on the next empty field or the last field
      const nextIndex = Math.min(index + digits.length, OTP_LENGTH - 1);
      digitRefs.current[nextIndex]?.focus();
    }
  };

  const handleDigitKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      digitRefs.current[index - 1]?.focus();
    }
  };

  const handleChangePhone = () => {
    setOtpSent(false);
    setOtpDigits(Array(OTP_LENGTH).fill(""));
    setOtp("");
    setOtpError("");
    setSendOtpError("");
    setSecondsLeft(0);
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < OTP_LENGTH) {
      setOtpError("Please enter the OTP");
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError("");
    try {
      const localPhone = getValues("phone_number") || "";
      const cleanedLocal = localPhone.replace(/\D/g, "");
      const fullPhone = `${countryCode}${cleanedLocal}`;
      await verifyOtp({ phone: fullPhone, otp, user_uuid: getValues("user_uuid") });
      setOtpVerified(true);
      setSecondsLeft(0);
    } catch (err) {
      console.error("verify otp failed", err);
      setOtpError(
        err?.response?.data?.message || "OTP verification failed. Please try again."
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleCompanyNameBlur = async () => {
    if (wasRecentlyCalled.current) {
      wasRecentlyCalled.current = false;
      return;
    }

    const companyName = getValues("company_name");
    if (!companyName) return null;

    setIsLoadingSuggestions(true);
    try {
      const res = await getDomainSuggestions({ company_name: companyName });
      setDomainSuggestions(res.data.unique_domain.map((domain, id) => ({ id, label: domain })));
      setValue("domain", res.data.unique_domain[0]);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  const debouncedBlurHandler = useCallback(
    debounce(() => {
      handleCompanyNameBlur();
      wasRecentlyCalled.current = true;
    }, 3000),
    []
  );

  const isAvailableDomain = async () => {
    const data = getValues();
    clearErrors("domain");

    if (data?.domain) {
      try {
        const res = await checkDomain(data);
        const { duplicate_domain } = res.data.data.responseData;

        if (duplicate_domain) {
          setError("domain", {
            type: "manual",
            message: "The domain you selected is already taken.",
          });
          return true;
        }
      } catch (err) {
        console.error(err);
      }
    }

    return false;
  };

  return (
    <Stack className="height100">
      <div className="steps_center-content">
        <Typography variant="h6" mb={4} style={{ fontSize: 24 }}>
          Company Information
        </Typography>
        <Stack spacing={3} flexGrow={"1"}>
          <FormTextField
            name={"company_name"}
            label="Company Name"
            onBlur={debouncedBlurHandler}
            onChange={handleCompanyNameChange}
          />
          <FormTextField
            name={"user_name"}
            label="Name"
          />
          <FormTextField
            name={"email"}
            label="Email Address"
            sx={{ flex: 1 }}
            onBlur={(e) => validateEmail(e.target.value)}
          />
          <Stack spacing={1}>
            {(!otpSent || otpVerified) && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  disabled={otpVerified}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="1">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <img
                        src="https://flagcdn.com/us.svg"
                        alt="US flag"
                        width={20}
                        height={14}
                        style={{ borderRadius: 2, objectFit: "cover" }}
                      />
                      <span>US</span>
                    </Stack>
                  </MenuItem>
                  <MenuItem value="91">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <img
                        src="https://flagcdn.com/in.svg"
                        alt="India flag"
                        width={20}
                        height={14}
                        style={{ borderRadius: 2, objectFit: "cover" }}
                      />
                      <span>IN</span>
                    </Stack>
                  </MenuItem>
                </Select>
                <FormTextField
                  name={"phone_number"}
                  label="Phone Number"
                  sx={{ flex: 1 }}
                  disabled={otpVerified}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {otpVerified ? (
                          <Typography variant="body2" color="success.main">
                            Phone number verified
                          </Typography>
                        ) : (
                          <LoadingButton
                            variant="text"
                            size="small"
                            onClick={handleSendOtp}
                            loading={isSendingOtp}
                            disabled={isSendingOtp || !isPhoneFormatValid}
                            sx={{ height: "32px", ml: 1, textTransform: "capitalize", textDecoration: "underline" }}
                          >
                            {otpSent ? "Resend OTP" : "Send OTP"}
                          </LoadingButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            )}
            
            {sendOtpError && (
              <Typography variant="body2" color="error">
                {sendOtpError}
              </Typography>
            )}
            <Collapse in={otpSent && !otpVerified} timeout="auto" unmountOnExit>
              <Stack
                spacing={1}
                sx={{
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: 3,
                  p: 2.5,
                  bgcolor: "background.paper",
                }}
              >
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    We've sent a 6-digit verification code to
                  </Typography>
                  <Stack direction="row" alignItems="center">
                      <Typography fontSize={16} color="text.secondary" fontWeight={600} sx={{ marginRight: '4px'}}>
                      {countryCode}{phoneValue}
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleChangePhone}
                      sx={{ textTransform: "capitalize", padding: 0 }}
                    >
                      Change Phone Number
                    </Button>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1}>
                  {Array.from({ length: OTP_LENGTH }).map((_, index) => (
                    <TextField
                      key={`otp-digit-${index}`}
                      inputRef={(el) => (digitRefs.current[index] = el)}
                      value={otpDigits[index]}
                      onChange={(e) => handleDigitChange(index, e.target.value)}
                      onPaste={(e) => handleDigitPaste(index, e)}
                      onKeyDown={(event) => handleDigitKeyDown(index, event)}
                      variant="outlined"
                      inputProps={{
                        maxLength: 1,
                        style: { textAlign: "center", fontSize: 18, padding: "12px" },
                      }}
                    />
                  ))}
                </Stack>
                <LoadingButton
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleVerifyOtp}
                  loading={isVerifyingOtp}
                  disabled={otpDigits.some((digit) => !digit) || isVerifyingOtp}
                  sx={{ textTransform: "capitalize" }}
                >
                  Verify OTP
                </LoadingButton>
                {otpError && (
                  <Typography variant="body2" color="error">
                    {otpError}
                  </Typography>
                )}
                <Stack direction="row" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Didn't receive the OTP?
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleSendOtp}
                    disabled={secondsLeft > 0 || isSendingOtp}
                    sx={{
                      textTransform: "capitalize",
                      textDecoration: "underline",
                    }}
                  >
                    Resend OTP
                  </Button>
                  {secondsLeft > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      in 00:{String(secondsLeft).padStart(2, "0")}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
          <Stack spacing={1}>
            <Autocomplete
              freeSolo
              disableClearable
              open={isOpen}
              onOpen={() => setIsOpen(true)}
              onClose={() => setIsOpen(false)}
              options={domainSuggestions || []}
              disabled={isLoadingSuggestions}
              getOptionLabel={(option) => option?.label || ''}
              value={isLoadingSuggestions ? { label: "Loading suggestions..." } : getValues("domain") ? { label: getValues("domain") } : null}
              onChange={(event, newValue) => {
                const value = typeof newValue === 'string' ? newValue : newValue?.label;
                setValue("domain", value);
                isAvailableDomain();
              }}
              renderInput={(params) => (
                <FormTextField
                  {...params}
                  name="domain"
                  label="Domain"
                  onBlur={isAvailableDomain}
                  onClick={() => setIsOpen(true)}
                  InputProps={{
                    ...params.InputProps,
                    sx: {
                      '&.MuiOutlinedInput-root': {
                        paddingRight: '8px !important'
                      }
                    },
                    startAdornment: isLoadingSuggestions ? (
                      <InputAdornment position="start">
                        <CircularProgress size={20} />
                      </InputAdornment>
                    ) : null,
                    endAdornment: (
                      <div
                        onClick={() => setIsOpen(true)}
                        className="domain_end_adornment"
                      >
                        <KeyboardArrowDown />
                        <InputAdornment position="end" className="domain-static">
                          .{domainUrl}
                        </InputAdornment>
                        {params.InputProps.endAdornment}
                      </div>
                    )
                  }}
                />
              )}
            />
          </Stack>
        </Stack>
      </div>

      <Stack direction="row" justifyContent="flex-end" className="">
        <Button
          variant="text"
          onClick={handleBack}
          sx={{ mr: 1 }}
          size="large"
        >
          cancel
        </Button>
        <LoadingButton
          variant="contained"
          size="large"
          type="submit"
          loading={isLoading}
          disabled={!otpVerified}>
          {"submit"}
        </LoadingButton>
      </Stack>
    </Stack>
  );
}

export default CompanyInfoComp;
